import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import useResetIntersection from 'components/custom-hooks/useResetIntersection'
import {
  ADDITIONAL_RELATED_SECTIONS,
  PDP_INTERACT_ELEM_EVENT_NAME,
  PDP_WISHLIST_REF_TYPE,
} from 'components/pdp/const'
import RelatedProductLayer from 'components/shared/product/product-related/related-product-layer'
import { withI18next } from 'lib/i18n/withI18next'
import { segmentTrackPDPInteract } from 'lib/utils/product'
import { MAX_RELATED_PRODUCT_NUM } from '../dynamic-section/const'
import getDynamicComponentData from '../dynamic-section/utils'
import RelatedProductSectionSkeleton from '../skeleton'
import widenMaxProduct from './utils'

const InfiniteSection = ({ auth, isPhone, product, t }) => {
  const containerRef = useRef()

  const baseMaxProduct = MAX_RELATED_PRODUCT_NUM[isPhone ? 'mobile' : 'desktop']
  const [maxProduct, setMaxProduct] = useState(baseMaxProduct)
  const updateMaxProduct = (number) => setMaxProduct(number)

  const productId = product?.id_product
  const determinerProps = {
    defaultState: 0,
    newDeterminer: productId,
  }
  const { isAllowToPerformIntersectionEvent: shouldTrackEvent, isVisible } =
    useResetIntersection(determinerProps, containerRef, {
      threshold: 0.3,
    })

  const { id, productList, translatedTitle } = getDynamicComponentData(
    auth,
    null,
    productId,
    t,
    ADDITIONAL_RELATED_SECTIONS[1],
  )
  const slicedProductList = useMemo(
    () => productList?.slice(0, maxProduct),
    [maxProduct, productList],
  )

  const isSectionValid = isVisible && productList?.length

  useEffect(() => {
    if (shouldTrackEvent) {
      segmentTrackPDPInteract(
        product,
        '',
        PDP_INTERACT_ELEM_EVENT_NAME.impression,
        {
          feedPos: 4,
          refType: PDP_WISHLIST_REF_TYPE.personalization,
          relatedProductList: productList,
        },
      )
    }
  }, [product, productList, shouldTrackEvent])

  useEffect(() => {
    // manually reset to baseMaxProduct when switch to another product, which won't caused the component to unmount
    if (!isVisible && maxProduct !== baseMaxProduct) {
      updateMaxProduct(baseMaxProduct)
    }
  }, [baseMaxProduct, maxProduct, isVisible])

  const customSkeletonProps = {
    number: 8,
    withCTA: false,
  }

  return (
    <div ref={containerRef}>
      {isSectionValid ? (
        <InfiniteScroll
          next={() =>
            widenMaxProduct({
              baseMaxProduct,
              maxProduct,
              productList,
              updateMaxProduct,
            })
          }
          dataLength={slicedProductList.length}
          scrollThreshold={isPhone ? 0.858 : 0.978}
          hasChildren={slicedProductList.length > 0}
          hasMore={slicedProductList.length < productList.length}
        >
          <RelatedProductLayer
            customSkeletonProps={customSkeletonProps}
            elementID={id}
            productId={productId}
            products={slicedProductList}
            refType={PDP_WISHLIST_REF_TYPE.personalization}
            title={translatedTitle}
          />
        </InfiniteScroll>
      ) : (
        <RelatedProductSectionSkeleton {...customSkeletonProps} />
      )}
    </div>
  )
}

InfiniteSection.defaultProps = {
  isPhone: false,
}

InfiniteSection.propTypes = {
  auth: PropTypes.shape({}).isRequired,
  isPhone: PropTypes.bool,
  product: PropTypes.shape({
    id_product: PropTypes.number,
  }).isRequired,
  t: PropTypes.func.isRequired,
}

export default compose(
  connect((state) => ({
    auth: state.auth,
    isPhone: state.device.isPhone,
  })),
  withI18next(),
)(InfiniteSection)
